import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Briefcase, Loader2, Plus, ExternalLink, Trash2, Eye, MousePointerClick, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../ThemeProvider';
import { useUserData } from '../UserDataContext';
import { businessTokens, statusPill } from './businessTheme';
import { BusinessRegistrationModal } from './BusinessRegistrationModal';
import { BusinessService } from '../../services/BusinessService';
import { openExternalUrl } from '../../utils/openExternal';
import {
  BusinessRegistration, BusinessProfile,
  BUSINESS_FEE_USD, MAX_BUSINESS_PROFILES, describeCooldown, toMillis,
} from '../../types/business';

/**
 * My Business — every registration and profile this account owns, in every
 * state at once.
 *
 * A user can genuinely be in several states simultaneously: one business live,
 * a second awaiting review, a third approved and unpaid. So this is a list of
 * rows rather than a single-state wizard, and each row carries only the action
 * that state actually affords.
 *
 * The Pay CTA never expires. An approval has no deadline, so an approved-unpaid
 * row keeps its Pay button indefinitely — this screen is the permanent home for
 * that button, which is why the approval notification points here.
 */

type Row =
  | { kind: 'registration'; registration: BusinessRegistration }
  | { kind: 'profile'; profile: BusinessProfile; registration?: BusinessRegistration };

export function MyBusinessScreen({ onBack }: { onBack: () => void }) {
  const { theme } = useTheme();
  const { userProfile } = useUserData();
  const navigate = useNavigate();
  const t = businessTokens(theme);

  const ownerId = String(userProfile?.id || '');

  const [registrations, setRegistrations] = useState<BusinessRegistration[]>([]);
  const [profiles, setProfiles] = useState<BusinessProfile[]>([]);
  const [cooldownUntil, setCooldownUntil] = useState<Date | null>(null);
  const [slotsUsed, setSlotsUsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!ownerId) return;
    const [regs, profs, cooldown, slots] = await Promise.all([
      BusinessService.getMyRegistrations(ownerId),
      BusinessService.getMyProfiles(ownerId),
      BusinessService.getCooldown(ownerId),
      BusinessService.getSlotUsage(ownerId),
    ]);
    setRegistrations(regs);
    setProfiles(profs);
    setCooldownUntil(cooldown);
    setSlotsUsed(slots.used);
    setLoading(false);
  }, [ownerId]);

  useEffect(() => { void load(); }, [load]);

  /**
   * One row per business, newest first.
   *
   * A registration that produced a profile is folded into that profile's row —
   * showing both would list the same business twice, once as paperwork and once
   * as the thing itself.
   */
  const rows: Row[] = useMemo(() => {
    const consumed = new Set(profiles.map(p => p.registrationId));
    const profileRows: Row[] = profiles.map(profile => ({
      kind: 'profile',
      profile,
      registration: registrations.find(r => r.id === profile.registrationId),
    }));
    const registrationRows: Row[] = registrations
      .filter(r => !consumed.has(r.id))
      .map(registration => ({ kind: 'registration', registration }));

    const stamp = (row: Row) => row.kind === 'profile'
      ? (toMillis(row.profile.createdAt) || 0)
      : (toMillis(row.registration.submittedAt) || 0);

    return [...profileRows, ...registrationRows].sort((a, b) => stamp(b) - stamp(a));
  }, [registrations, profiles]);

  const hasPending = registrations.some(r => r.status === 'pending');
  const coolingLabel = describeCooldown(cooldownUntil);
  const atCap = slotsUsed >= MAX_BUSINESS_PROFILES;
  const canRegister = !hasPending && !coolingLabel && !atCap;

  // ── Payment ────────────────────────────────────────────────────────────────
  //
  // Open Checkout externally, then watch the registration document for paidAt.
  // No return deep link: the listener fires whenever the webhook lands and the
  // user is back, so nothing depends on a redirect URL resolving correctly.
  const handlePay = async (registration: BusinessRegistration) => {
    if (payingId) return;
    setPayingId(registration.id);
    const res = await BusinessService.createCheckoutSession(registration.id);
    if (!res.success || !res.data) {
      setPayingId(null);
      toast.error(res.error || 'Could not start checkout.');
      return;
    }

    if (!openExternalUrl(res.data.url)) {
      setPayingId(null);
      toast.error('Your browser blocked the payment window. Allow pop-ups and try again.');
      return;
    }

    const unwatch = BusinessService.watchRegistration(registration.id, (updated) => {
      if (!updated?.paidAt) return;
      unwatch();
      setPayingId(null);
      toast.success('Payment received. Time to build your profile.');
      navigate(`/my-business/build/${registration.id}`);
    });

    // Stop waiting after ten minutes so an abandoned checkout does not leave a
    // listener and a spinner running for the rest of the session. The payment is
    // not lost if this fires: paidAt is on the document, and the next visit to
    // this screen shows the Build button.
    setTimeout(() => { unwatch(); setPayingId(prev => (prev === registration.id ? null : prev)); }, 10 * 60 * 1000);
  };

  const handleDelete = async (profile: BusinessProfile) => {
    const confirmed = window.confirm(
      `Delete ${profile.businessName}? Its posts and store go with it, this cannot be undone, ` +
      `and you will not be able to register a new business for a while afterwards.`,
    );
    if (!confirmed) return;
    setDeletingId(profile.id);
    const res = await BusinessService.deleteMyProfile(profile.id, ownerId);
    setDeletingId(null);
    if (!res.success) { toast.error(res.error || 'Could not delete that profile.'); return; }
    toast.success('Business profile deleted.');
    await load();
  };

  const cardStyle: React.CSSProperties = {
    background: t.surface,
    border: `1px solid ${t.border}`,
    borderRadius: 18,
    padding: 16,
    marginBottom: 12,
  };

  const primaryButton: React.CSSProperties = {
    height: 42, padding: '0 18px', borderRadius: 12, border: 'none',
    background: t.buttonBg, color: t.buttonText, fontSize: 14, fontWeight: 700, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  };

  const ghostButton: React.CSSProperties = {
    height: 42, padding: '0 16px', borderRadius: 12,
    background: t.inset, border: `1px solid ${t.border}`, color: t.text,
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  };

  return (
    <div style={{ width: '100%', height: '100vh', background: t.bg, overflowY: 'auto' }}>
      <div
        style={{
          position: 'sticky', top: 0, zIndex: 5,
          background: t.bg, borderBottom: `1px solid ${t.border}`,
          padding: '0 16px 16px',
          paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onBack}
            aria-label="Go back"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.text, padding: 0 }}
          >
            <ArrowLeft style={{ width: 24, height: 24 }} />
          </motion.button>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: t.text }}>My Business</h1>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: t.textMuted }}>
              {rows.length === 0
                ? 'Nothing here yet'
                : `${slotsUsed} of ${MAX_BUSINESS_PROFILES} businesses`}
            </p>
          </div>
        </div>
      </div>

      <div style={{ padding: '16px 16px 96px', maxWidth: 640, margin: '0 auto' }}>
        {loading ? (
          <div style={{ padding: 48, display: 'flex', justifyContent: 'center' }}>
            <Loader2 style={{ width: 28, height: 28, color: t.accent }} className="animate-spin" />
          </div>
        ) : (
          <>
            {rows.length === 0 && (
              <div style={{ ...cardStyle, textAlign: 'center', padding: 32 }}>
                <Briefcase style={{ width: 34, height: 34, color: t.accent, margin: '0 auto 12px' }} />
                <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: t.text }}>
                  Put your business on AfroYap
                </p>
                <p style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.5, color: t.textMuted }}>
                  A portfolio, your own posts, and a store that sends people straight to
                  where they can buy. Apply first: we read every application, and
                  the one-time ${BUSINESS_FEE_USD} fee is only charged if you are approved.
                </p>
              </div>
            )}

            {rows.map(row => (
              row.kind === 'profile'
                ? (
                  <ProfileRow
                    key={row.profile.id}
                    t={t}
                    profile={row.profile}
                    deleting={deletingId === row.profile.id}
                    cardStyle={cardStyle}
                    primaryButton={primaryButton}
                    ghostButton={ghostButton}
                    onOpen={() => navigate(`/business/${row.profile.id}`)}
                    onDelete={() => handleDelete(row.profile)}
                  />
                )
                : (
                  <RegistrationRow
                    key={row.registration.id}
                    t={t}
                    registration={row.registration}
                    paying={payingId === row.registration.id}
                    cardStyle={cardStyle}
                    primaryButton={primaryButton}
                    onPay={() => handlePay(row.registration)}
                    onBuild={() => navigate(`/my-business/build/${row.registration.id}`)}
                  />
                )
            ))}

            <button
              type="button"
              onClick={() => setShowRegister(true)}
              disabled={!canRegister}
              style={{
                ...primaryButton,
                width: '100%', height: 50, marginTop: 8,
                opacity: canRegister ? 1 : 0.5,
                cursor: canRegister ? 'pointer' : 'default',
              }}
            >
              <Plus style={{ width: 18, height: 18 }} />
              Register a business
            </button>

            {/* One reason at a time, in the order the user would hit them. */}
            {atCap ? (
              <p style={{ margin: '10px 0 0', fontSize: 12, color: t.textMuted, textAlign: 'center' }}>
                You can run {MAX_BUSINESS_PROFILES} businesses at a time. Delete one to apply
                for another.
              </p>
            ) : hasPending ? (
              <p style={{ margin: '10px 0 0', fontSize: 12, color: t.textMuted, textAlign: 'center' }}>
                You already have an application in review. You can apply for another business
                once this one is decided.
              </p>
            ) : coolingLabel ? (
              <p style={{ margin: '10px 0 0', fontSize: 12, color: t.textMuted, textAlign: 'center' }}>
                You can apply again in {coolingLabel}.
              </p>
            ) : null}
          </>
        )}
      </div>

      <BusinessRegistrationModal
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
        ownerId={ownerId}
        cooldownUntil={cooldownUntil}
        onSubmitted={() => void load()}
      />
    </div>
  );
}

// ── Rows ─────────────────────────────────────────────────────────────────────

function Pill({ t, status }: { t: ReturnType<typeof businessTokens>; status: string }) {
  const pill = statusPill(t, status);
  return (
    <span
      style={{
        background: pill.bg, color: pill.fg,
        fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
        whiteSpace: 'nowrap',
      }}
    >
      {pill.label}
    </span>
  );
}

function RegistrationRow({
  t, registration, paying, cardStyle, primaryButton, onPay, onBuild,
}: {
  t: ReturnType<typeof businessTokens>;
  registration: BusinessRegistration;
  paying: boolean;
  cardStyle: React.CSSProperties;
  primaryButton: React.CSSProperties;
  onPay: () => void;
  onBuild: () => void;
}) {
  const paid = !!registration.paidAt;
  const deleted = !!(registration as any).profileDeletedAt;
  const cooling = describeCooldown(registration.cooldownExpiresAt);

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <Briefcase style={{ width: 18, height: 18, color: t.accent, flexShrink: 0 }} />
        <span
          style={{
            flex: 1, fontSize: 15, fontWeight: 700, color: t.text,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {registration.businessName || 'Application'}
        </span>
        <Pill t={t} status={deleted ? 'deleted' : (paid ? 'paid' : registration.status)} />
      </div>

      <p
        style={{
          margin: '0 0 10px', fontSize: 13, lineHeight: 1.5, color: t.textMuted,
          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}
      >
        {registration.pitchText}
      </p>

      {registration.status === 'denied' && registration.denialReason && (
        <div
          style={{
            background: 'rgba(231,76,60,0.10)', border: `1px solid ${t.danger}33`,
            borderRadius: 10, padding: 10, marginBottom: 10,
          }}
        >
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: t.danger }}>Why it was declined</p>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: t.text }}>{registration.denialReason}</p>
          {cooling && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: t.textMuted }}>
              You can apply again in {cooling}.
            </p>
          )}
        </div>
      )}

      {deleted && (
        <p style={{ margin: '0 0 10px', fontSize: 12, color: t.textMuted }}>
          The profile built from this application was deleted. Registering again means a new
          application and a new fee.
        </p>
      )}

      {registration.status === 'approved' && !deleted && (
        paid ? (
          <button type="button" onClick={onBuild} style={{ ...primaryButton, width: '100%' }}>
            Build your profile
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={onPay}
              disabled={paying}
              style={{ ...primaryButton, width: '100%', opacity: paying ? 0.7 : 1 }}
            >
              {paying
                ? (<><Loader2 style={{ width: 16, height: 16 }} className="animate-spin" /> Waiting for payment</>)
                : `Pay $${BUSINESS_FEE_USD}`}
            </button>
            <p style={{ margin: '8px 0 0', fontSize: 11, color: t.textMuted, textAlign: 'center' }}>
              {paying
                ? 'Finish in the payment window. This page updates on its own once it clears.'
                : 'One-time fee. Your approval does not expire, so you can pay whenever you are ready.'}
            </p>
          </>
        )
      )}
    </div>
  );
}

function ProfileRow({
  t, profile, deleting, cardStyle, primaryButton, ghostButton, onOpen, onDelete,
}: {
  t: ReturnType<typeof businessTokens>;
  profile: BusinessProfile;
  deleting: boolean;
  cardStyle: React.CSSProperties;
  primaryButton: React.CSSProperties;
  ghostButton: React.CSSProperties;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const stat = (Icon: any, value: number, label: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }} title={label}>
      <Icon style={{ width: 13, height: 13, color: t.textMuted }} />
      <span style={{ fontSize: 12, color: t.textMuted }}>{value ?? 0}</span>
    </div>
  );

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        {profile.portfolioImages?.[0] && (
          <img
            src={profile.portfolioImages[0]}
            alt=""
            style={{ width: 46, height: 46, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: 0, fontSize: 16, fontWeight: 700, color: t.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {profile.businessName}
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
            {stat(Eye, profile.viewCount, 'Profile views')}
            {stat(MousePointerClick, profile.storeTapCount, 'Store link taps')}
            {stat(ShoppingBag, profile.confirmedPurchaseCount, 'Confirmed purchases')}
          </div>
        </div>
        <Pill t={t} status={profile.status} />
      </div>

      {profile.status === 'suspended' && (
        <div
          style={{
            background: 'rgba(231,76,60,0.10)', border: `1px solid ${t.danger}33`,
            borderRadius: 10, padding: 10, marginBottom: 10,
          }}
        >
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: t.danger }}>Suspended</p>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: t.text }}>
            {profile.suspendReason || 'This profile is hidden while it is under review.'}
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 12, color: t.textMuted }}>
            Your account and any other business profiles you own are unaffected.
          </p>
        </div>
      )}

      {profile.pendingEdit && (
        <p style={{ margin: '0 0 10px', fontSize: 12, color: t.warning }}>
          A name or link change is waiting on review. Your profile stays live with its current
          details until it is decided.
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={onOpen} style={{ ...primaryButton, flex: 1, minWidth: 140 }}>
          Open profile
        </button>
        <button
          type="button"
          onClick={() => openExternalUrl(profile.shopLink)}
          style={ghostButton}
          aria-label="Open shop link"
        >
          <ExternalLink style={{ width: 15, height: 15 }} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={deleting}
          style={{ ...ghostButton, color: t.danger, opacity: deleting ? 0.6 : 1 }}
          aria-label="Delete this business profile"
        >
          {deleting
            ? <Loader2 style={{ width: 15, height: 15 }} className="animate-spin" />
            : <Trash2 style={{ width: 15, height: 15 }} />}
        </button>
      </div>
    </div>
  );
}
